import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { triggerNotificationHaptic } from '../../../assets/utils/hapticFeedback';
import './PinDots.css';

const ERROR_SHAKE_ANIMATION = { x: [0, -8, 8, -6, 6, -3, 3, 0] };
const ERROR_SHAKE_TRANSITION = { duration: 0.42, ease: 'easeInOut' };
const FILL_DOT_ANIMATION = { scale: [1, 1.28, 0.92, 1.08, 1] };
const FILL_DOT_TRANSITION = {
  duration: 0.36,
  times: [0, 0.34, 0.58, 0.78, 1],
  ease: [0.22, 1, 0.36, 1],
};
const SUCCESS_DOT_ANIMATION = { scale: [1, 1.2, 1] };
const SUCCESS_DOT_TRANSITION = {
  duration: 0.36,
  times: [0, 0.5, 1],
  ease: [0.22, 1, 0.36, 1],
};
const HANDOFF_DOT_ANIMATION = {
  scale: [1, 1.18, 0.9, 1],
  y: [0, -6, 2, 0],
};
const HANDOFF_DOT_TRANSITION = {
  duration: 0.42,
  times: [0, 0.34, 0.68, 1],
  ease: [0.22, 1, 0.36, 1],
};

function PinDots({
  length,
  filledCount,
  hasError = false,
  errorKey = 0,
  isProcessing = false,
  isSuccess = false,
  isHandoff = false,
  className,
}) {
  const [isErrorActive, setIsErrorActive] = useState(false);
  const [animatedDotIndex, setAnimatedDotIndex] = useState(null);
  const previousFilledCountRef = useRef(filledCount);
  const shouldReduceMotion = useReducedMotion();
  const classes = [
    'pin-dots',
    className,
    isErrorActive ? 'pin-dots--error' : null,
    isProcessing ? 'pin-dots--processing' : null,
    isSuccess ? 'pin-dots--success' : null,
    isHandoff ? 'pin-dots--handoff' : null,
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    const previousFilledCount = previousFilledCountRef.current;

    if (filledCount > previousFilledCount) {
      setAnimatedDotIndex(filledCount - 1);
    } else {
      setAnimatedDotIndex(null);
    }

    previousFilledCountRef.current = filledCount;
  }, [filledCount]);

  useEffect(() => {
    if (!hasError) {
      setIsErrorActive(false);
      return undefined;
    }

    triggerNotificationHaptic('error');
    setIsErrorActive(true);
    const timeoutId = window.setTimeout(() => {
      setIsErrorActive(false);
    }, 650);

    return () => window.clearTimeout(timeoutId);
  }, [hasError, errorKey]);

  return (
    <motion.div
      className={classes}
      aria-hidden="true"
      animate={isErrorActive && !shouldReduceMotion ? ERROR_SHAKE_ANIMATION : { x: 0 }}
      transition={ERROR_SHAKE_TRANSITION}
    >
      {Array.from({ length }, (_, index) => {
        const isFilled = index < filledCount;
        const shouldAnimateHandoff =
          isFilled && isHandoff && !shouldReduceMotion && !isErrorActive;
        const shouldAnimateSuccess =
          isFilled && !isHandoff && isSuccess && !shouldReduceMotion && !isErrorActive;
        const shouldAnimateFill =
          isFilled &&
          !isHandoff &&
          !isProcessing &&
          !isSuccess &&
          index === animatedDotIndex &&
          !shouldReduceMotion &&
          !isErrorActive;
        let dotAnimation = { scale: 1 };
        let dotTransition;

        if (shouldAnimateHandoff) {
          dotAnimation = HANDOFF_DOT_ANIMATION;
          dotTransition = {
            ...HANDOFF_DOT_TRANSITION,
            delay: index * 0.035,
          };
        } else if (shouldAnimateSuccess) {
          dotAnimation = SUCCESS_DOT_ANIMATION;
          dotTransition = {
            ...SUCCESS_DOT_TRANSITION,
            delay: index * 0.035,
          };
        } else if (shouldAnimateFill) {
          dotAnimation = FILL_DOT_ANIMATION;
          dotTransition = FILL_DOT_TRANSITION;
        }

        return (
          <motion.span
            key={index}
            className={`pin-dots__dot${isFilled ? ' pin-dots__dot--filled' : ''}`}
            animate={dotAnimation}
            transition={dotTransition}
          />
        );
      })}
    </motion.div>
  );
}

export default PinDots;
